// Discount pop-ups (M4-F6, M3-F8; rows M4.19 / M4.22 / M4.26, M3.22 / M3.28–M3.30; DC-81 / DC-85 / DC-91 / DC-92 /
// DC-105 / DC-106 / DC-109 / DC-112 / DC-153). Same order and words as the portal prototype:
//   gift   "Good news" (loyalty offer, or a discount applied)          prototype mGift
//   disc   check result after every KYC submit; for an open loyalty offer the confirmation with both
//          choices side by side                                         prototype mDisc / mLoyConfirm
//   final  "Please confirm your choice" (amounts from the server)        prototype mLoyFinal
//   pick   "Please confirm your choice" for Add back / Change to Referral prototype mPick2
// view = discountPreview from the KYC submit, or the server's portal view (card links). Nothing is decided
// automatically; closing with the cross keeps the decision open.
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import { decideCrm2Discount, getCrm2Discounts } from "../../../Services/Crm2Portal";
import { errorTost, successTost } from "../../../Services/Toast";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import Crm2KycModal from "./Crm2KycModal";
import { inr } from "./Crm2Parts";
import css from "./Crm2Booking.module.css";

const SDRN = "Stamp Duty, Registration and Legal Charges";
const LEADN = { LOYALTY: "Loyalty", REFERRAL: "Referral", DIRECT: "Direct" };
const CHIP = { ok: css.cOk, wa: css.cWa, in: css.cIn, er: css.cEr, g: css.cG };
const GIFT_SVG = (
	<svg viewBox="0 0 24 24">
		<path d="M12 2.8l2.6 2 3.2-.3.9 3.1 2.7 1.8-1.2 3 1.2 3-2.7 1.8-.9 3.1-3.2-.3-2.6 2-2.6-2-3.2.3-.9-3.1L2.6 15.4l1.2-3-1.2-3 2.7-1.8.9-3.1 3.2.3z" />
		<path d="M8.5 12.2l2.4 2.4 4.7-4.8" />
	</svg>
);

const Chip = ({ text, tone = "g" }) => <span className={`${css.chip} ${CHIP[tone] || ""}`}>{text}</span>;
const caseOf = (l) => (l?.state === "OFFERED" ? "offer" : l?.state === "MISSING" ? "missing" : l?.state === "REMOVED" ? "removed" : null);

function Shell({ title, wide, onClose, foot, children }) {
	return (
		<Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
			<Box className={css.modal} style={wide ? { width: "72rem" } : undefined}>
				<div className={css.modalHeader}>
					<span>{title}</span>
					<span className={css.modalClose} onClick={onClose} tabIndex={0}>
						<Close width={"1.2rem"} height={"1.2rem"} />
					</span>
				</div>
				<div className={css.modalContent} style={{ display: "flex", flexDirection: "column", gap: "1.4rem" }}>
					{children}
				</div>
				<div className={css.modalFooter}>{foot}</div>
			</Box>
		</Modal>
	);
}

const Btn = ({ primary, onClick, children }) => (
	<button type="button" className={`${css.btn} ${primary ? css.btnP : ""}`} onClick={onClick}>
		{children}
	</button>
);

function UnitHead({ card, lead }) {
	return (
		<div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
			<b style={{ fontSize: "1.5rem", color: "#101828" }}>
				{card?.unit?.label}
				{card?.unit?.projectName ? ` · ${card.unit.projectName}` : ""}
			</b>
			<Chip text={`${LEADN[lead] || "Direct"} booking`} />
		</div>
	);
}

/**
 * The whole discount pop-up flow.
 * props: apId, card, view, start ("auto" | "gift" | "disc" | "final:loy" | "final:keep" | "pick:addback" | "pick:toref"),
 *        kyc (KYC submit result, for the title and next-step line), onClose(), onDone(), onAddBack()
 */
export function DiscountFlow({ apId, card, view, start = "auto", kyc = null, onClose, onDone, onAddBack }) {
	const L = view?.loyalty || {};
	const F = view?.female || {};
	const cs = caseOf(L);
	const lead = L.lead || "DIRECT";
	const ref = lead === "REFERRAL";
	const ex = L.existingCustomer || "the existing VJ customer";
	const fn = String(ex).split(" ")[0];
	const ptsTxt = L.points ? `${Number(L.points).toLocaleString("en-IN")} VJOP points` : "VJOP referral points";
	const before = view?.before || {};
	const after = view?.after || {};
	const withLoy = view?.withLoyalty || null;
	const d = (after.total || 0) - (before.total || 0);

	const first = () => {
		if (start !== "auto") return start;
		const lost = F.state === "LOST" || cs === "missing" || cs === "removed";
		if (lost) return "disc";
		if (cs === "offer") return "gift";
		if (F.state === "GAINED") return "gift";
		return "disc"; // DC-112: the result always shows
	};
	const [step, setStep] = useState(first);
	const [effect, setEffect] = useState(null); // server preview of the chosen option
	const [addBack, setAddBack] = useState(false);

	const [kind, pick] = String(step).split(":");
	const decisionOf = (p) => (p === "loy" ? (ref ? "avail_discount" : "approve") : p === "keep" ? (ref ? "keep_referral" : "not_approve") : p === "toref" ? "change_to_referral" : "add_back");

	useEffect(() => {
		if (kind !== "final" && !(kind === "pick" && pick === "toref")) return;
		setEffect(null);
		decideCrm2Discount(apId, decisionOf(pick), false)
			.then(setEffect)
			.catch((err) => {
				errorTost(err.message);
				setStep("disc");
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apId, step]);

	const confirm = (p) => {
		presentLoader();
		decideCrm2Discount(apId, decisionOf(p), true)
			.then((res) => {
				dismissLoader();
				console.log("[crm2][discount] decided", { decision: res?.decision, bookingType: res?.bookingType });
				if (p === "addback") {
					// back to Edit Agreement KYC (the check runs again on save)
					if (onAddBack) onAddBack();
					else setAddBack(true);
					return;
				}
				successTost(
					p === "loy"
						? `Loyalty discount applied: ${inr(L.amount)} off your total unit cost. Your review draft is made with the new price; please read and confirm it.`
						: p === "toref"
							? `Your booking is now a Referral booking. ${fn} earns ${ptsTxt} in VJOP after the agreement is registered.`
							: ref
								? `Referral kept. ${ex} earns ${ptsTxt} in VJOP after the agreement is registered.`
								: "Loyalty discount not approved. Your booking stays at the actual unit price."
				);
				onDone();
			})
			.catch((err) => {
				dismissLoader();
				errorTost(err.message);
			});
	};

	if (addBack) return <Crm2KycModal allotment={{ ...(card?.allotment || {}), allotmentPaymentId: apId }} card={card} mode="edit" onClose={onDone} onDone={onDone} />;

	// ---- gift (mGift)
	if (kind === "gift") {
		const ch = cs === "offer";
		const tgt = ch ? withLoy || after : after;
		const save = (before.total || 0) - (tgt.total || 0);
		const fem = F.state === "GAINED";
		const loy = ch;
		const head = ch && fem ? "Two discounts for you" : ch ? "You are eligible for a loyalty discount" : loy && fem ? "Two discounts for you" : loy ? "A loyalty discount for you" : "You save on stamp duty";
		return (
			<Shell
				title=""
				onClose={onClose}
				foot={
					ch ? (
						<>
							{ref && <Btn onClick={() => setStep("final:keep")}>Keep referral</Btn>}
							<Btn onClick={() => setStep("disc")}>View details</Btn>
							<Btn primary onClick={() => setStep("final:loy")}>Avail discount</Btn>
						</>
					) : (
						<>
							<Btn onClick={() => setStep("disc")}>View details</Btn>
							<Btn primary onClick={onDone}>Continue</Btn>
						</>
					)
				}
			>
				<div className={css.gift}>
					<div className={css.gic}>{GIFT_SVG}</div>
					<div className={css.gl}>Good news</div>
					<h3>{head}</h3>
					<div className={css.gs}>
						{ch ? "Save" : "You save"} <b>{inr(save)}</b>
					</div>
					{loy && (
						<div className={css.gi}>
							<span className={css.gk}>✓</span>
							<div>
								<b>Loyalty discount from VJ</b>
								<span>Thank you for being a VJ customer · {inr(L.amount)} off your total unit cost</span>
							</div>
						</div>
					)}
					{fem && (
						<div className={css.gi}>
							<span className={css.gk}>✓</span>
							<div>
								<b>Female 1% stamp duty discount</b>
								<span>
									Allowed by the Government of Maharashtra · stamp duty {F.sdrFrom}% → {F.sdrTo}%
								</span>
							</div>
						</div>
					)}
					<div className={css.gt}>
						Total unit cost <s>{inr(before.total)}</s> <b>{inr(tgt.total)}</b>
					</div>
					<div className={css.gn}>
						{ch ? `${ex} is an existing VJ customer on your booking.${ref ? ` Or keep the referral: ${fn} earns ${ptsTxt} in VJOP after the agreement is registered.` : " Avail it to apply it to your booking."}` : "Applied to your booking. Your new cost sheet follows."}
					</div>
				</div>
			</Shell>
		);
	}

	// ---- confirmation with both choices (mLoyConfirm)
	if (kind === "disc" && cs === "offer") {
		const w = withLoy || after;
		const save = (after.total || 0) - (w.total || 0);
		const row = (l, x, y, strong) => (
			<tr className={strong ? css.lcT : undefined}>
				<td>{l}</td>
				<td className={css.rt}>{typeof x === "number" ? inr(x) : x}</td>
				<td className={`${css.rt} ${typeof y === "number" && y < x ? css.cdn : ""}`}>{typeof y === "number" ? inr(y) : y}</td>
			</tr>
		);
		return (
			<Shell
				title={ref ? "Confirm your booking type" : "Confirm your loyalty discount"}
				wide
				onClose={onClose}
				foot={
					<>
						<Btn onClick={() => setStep("final:keep")}>{ref ? "Not approve · keep referral" : "Not approve"}</Btn>
						<Btn primary onClick={() => setStep("final:loy")}>
							Approve and proceed
						</Btn>
					</>
				}
			>
				<UnitHead card={card} lead={lead} />
				<div className={css.info}>
					<b>{ex} (existing VJ customer) is an applicant in your agreement.</b>{" "}
					{ref ? `${fn} referred you, so your booking is a Referral booking. Because ${fn} is also your co-applicant, you can choose a loyalty discount instead.` : "So your Loyalty booking can get the loyalty discount."} Please choose one; your review draft and cost sheet are made with the price you choose.
				</div>
				<div className={css.lcG}>
					<div className={`${css.lcO} ${css.lcOk}`}>
						<div className={css.lcH}>
							<b>Approve loyalty discount</b>
							<Chip text={`Save ${inr(save)}`} tone="ok" />
						</div>
						<ul>
							<li>
								Booking type: <b>Loyalty</b>
								{ref ? " (changes from Referral, in the Sales App and VJOP)" : ""}
							</li>
							<li>
								Total unit cost <b>{inr(w.total)}</b> ({inr(L.amount)} off)
							</li>
							<li>{ref ? <>{fn} gets <b>no VJOP points</b> ({ptsTxt} not earned)</> : "Loyalty discount from VJ for existing customers"}</li>
							<li>Agreement value, GST and stamp duty worked back from the new total</li>
						</ul>
					</div>
					<div className={css.lcO}>
						<div className={css.lcH}>
							<b>{ref ? "Not approve · keep referral" : "Not approve"}</b>
							<Chip text="Actual unit price" />
						</div>
						<ul>
							<li>
								Booking type stays <b>{LEADN[lead]}</b>
							</li>
							<li>
								Total unit cost <b>{inr(after.total)}</b>
							</li>
							<li>{ref ? <>{fn} earns <b>{ptsTxt}</b> in VJOP, credited after the agreement is registered</> : "No loyalty discount"}</li>
							<li>Nothing else changes</li>
						</ul>
					</div>
				</div>
				<div className={css.panel}>
					<h4>Unit cost</h4>
					<div className={css.tw}>
						<table className={css.tbl}>
							<tbody>
								<tr>
									<th>Item</th>
									<th className={css.rt}>Actual unit price</th>
									<th className={css.rt}>Unit price after loyalty discount</th>
								</tr>
								{row("Agreement value", after.av, w.av)}
								{row("GST", after.gst, w.gst)}
								{row(SDRN, after.sdr, w.sdr)}
								{row(<b>Total unit cost</b>, after.total, w.total, true)}
								{row("Booking type", LEADN[lead], "Loyalty")}
								{ref && row(`VJOP referral points for ${fn} (after the agreement is registered)`, Number(L.points || 0).toLocaleString("en-IN"), "0")}
							</tbody>
						</table>
					</div>
				</div>
				<p className={css.muted}>Next: your Agreement Review (review draft) opens with the price you choose; confirm it by OTP. Until you choose, your next steps stay locked. No action is needed from your CRM.</p>
			</Shell>
		);
	}

	// ---- check result (mDisc)
	if (kind === "disc") {
		const femAmt = F.state === "GAINED" ? (before.sdr || 0) - (after.sdr || 0) : F.state === "LOST" ? (after.sdr || 0) - (before.sdr || 0) : 0;
		const loyRow =
			L.state === "REMOVED_BY_VJ"
				? ["Removed by VJ", "er", "Loyalty discount · removed by VJ. It is not offered again for this booking."]
				: lead === "DIRECT"
					? ["Not applicable", "g", "Your booking is a direct booking (not Loyalty or Referral)."]
					: cs === "removed"
						? ["Removed", "er", `You removed ${ex}, the existing VJ customer, from your applicants.`]
						: cs === "missing"
							? ["Not applied", "er", `${ex}, the existing VJ customer, is not an applicant.`]
							: L.state === "CHECK_FAILED"
								? ["Check pending", "wa", "We could not check the loyalty discount just now. We check again shortly and tell you."]
								: L.state === "ALREADY"
									? ["Already applied", "in", `${inr(L.amount)} off your total unit cost, because ${ex} (existing VJ customer) is an applicant. Agreement value, GST and stamp duty are worked out again from the new total.`]
									: ref && L.decision === "keep_referral"
										? ["Referral kept", "in", `${ex} earns ${ptsTxt} in VJOP after the agreement is registered. No loyalty discount.`]
										: ref
											? ["Not applicable", "g", `Referral booking: ${ex} earns ${ptsTxt} in VJOP after the agreement is registered.`]
											: L.decision === "not_approve"
												? ["Not taken", "g", "You chose not to take the loyalty discount. Your unit stays at the actual unit price."]
												: ["Not applicable", "g", "No loyalty discount applies to this booking."];
		const femRow =
			F.state === "GAINED"
				? ["Applied", "ok", `Only ${SDRN} go down: ${inr(femAmt)} less (stamp duty ${F.sdrFrom}% → ${F.sdrTo}%). Your agreement value does not change.`]
				: F.state === "ALREADY"
					? ["Already applied", "in", `1% off the stamp duty only (${F.sdrFrom}% → ${F.sdrTo}%) · agreement value unchanged.`]
					: F.state === "LOST"
						? ["Removed", "er", `You changed your applicants: the rule is no longer met (this project's rule: ${F.rule} is female). You lose the 1% female discount: ${inr(femAmt)} more on your ${SDRN} payment.`]
						: ["Not applicable", "g", `The ${F.rule} is not female (this project's rule: ${F.rule} is female).`];
		const showLoy = lead !== "DIRECT" && (["ALREADY", "REMOVED", "MISSING", "REMOVED_BY_VJ", "CHECK_FAILED"].includes(L.state) || !!L.decision);
		const showFem = ["GAINED", "ALREADY", "LOST"].includes(F.state);
		const bFem = F.state === "LOST" || F.state === "ALREADY";
		const aFem = F.state === "GAINED" || F.state === "ALREADY";
		const bLoy = L.state === "ALREADY" || L.state === "REMOVED";
		const aLoy = L.state === "ALREADY";
		const dn = (loy, fem) => (loy && fem ? "Unit price after discounts" : loy ? "Unit price after loyalty discount" : fem ? "Unit price after female 1% discount" : "Actual unit price");
		const line = (l, x, y) => (
			<tr>
				<td>{l}</td>
				<td className={css.rt}>{inr(x)}</td>
				<td className={`${css.rt} ${y < x ? css.cdn : y > x ? css.cup : ""}`}>{inr(y)}</td>
			</tr>
		);
		const title = kyc ? (kyc.nothingChanged ? "Agreement KYC submitted again" : kyc.edit ? "Changes saved" : "Agreement KYC submitted") : "Discount checks";
		return (
			<Shell
				title={title}
				wide={!!d}
				onClose={cs ? onClose : onDone}
				foot={
					cs ? (
						<>
							<Btn onClick={() => setStep("pick:addback")}>{`Add ${fn} back`}</Btn>
							<Btn primary onClick={() => setStep("pick:toref")}>
								Change to Referral
							</Btn>
						</>
					) : (
						<Btn primary onClick={onDone}>
							OK
						</Btn>
					)
				}
			>
				<UnitHead card={card} lead={lead} />
				<p className={css.muted}>
					{kyc?.nothingChanged ? "Nothing changed in your KYC. We ran the loyalty and female 1% checks again; the result is below." : kyc?.edit ? "We saved your changes and checked your KYC again." : "We checked your KYC."} These checks run after every submit or change.
				</p>
				{cs ? (
					<div className={css.badBox}>
						<b>{cs === "removed" ? `You have removed the existing customer ${ex} as an applicant from your agreement.` : `${ex}, the existing VJ customer, is not an applicant in your agreement.`}</b> So we {cs === "removed" ? "will remove" : "cannot give"} your loyalty discount
						{d > 0 ? ` (your total unit cost goes up by ${inr(d)})` : ""}. You may change your booking from Loyalty to Referral, so {ex} earns {ptsTxt} in VJOP after the agreement is registered. Or add {ex} back as an applicant.
					</div>
				) : d < 0 ? (
					<div className={css.good}>
						<b>Your total unit cost has changed.</b> It goes down by {inr(-d)}: {inr(before.total)} → {inr(after.total)}.
					</div>
				) : d > 0 ? (
					<div className={css.badBox}>
						<b>Your total unit cost will increase by {inr(d)}.</b> {inr(before.total)} → {inr(after.total)}, because a discount you had no longer applies.
					</div>
				) : (
					<div className={css.info}>No change to your total unit cost ({inr(before.total)}).</div>
				)}
				<div className={css.panel} style={{ gap: "1rem" }}>
					{showLoy || showFem ? <h4>Your discounts</h4> : <div className={css.muted}>No discount applies to this booking.</div>}
					{showLoy && (
						<div className={css.dchk}>
							<div>
								<b>Loyalty discount</b>
								<span>{loyRow[2]}</span>
							</div>
							<Chip text={loyRow[0]} tone={loyRow[1]} />
						</div>
					)}
					{showFem && (
						<div className={css.dchk}>
							<div>
								<b>Female 1% stamp duty discount</b>
								<span>{femRow[2]}</span>
							</div>
							<Chip text={femRow[0]} tone={femRow[1]} />
						</div>
					)}
				</div>
				{!!d && (
					<div className={css.panel}>
						<h4>Unit cost</h4>
						<div className={css.tw}>
							<table className={css.tbl}>
								<tbody>
									<tr>
										<th>Item</th>
										<th className={css.rt}>{dn(bLoy, bFem)}</th>
										<th className={css.rt}>{dn(aLoy, aFem)}</th>
									</tr>
									{line("Agreement value", before.av, after.av)}
									{line("GST", before.gst, after.gst)}
									{line(SDRN, before.sdr, after.sdr)}
									{line(<b>Total Unit Cost</b>, before.total, after.total)}
								</tbody>
							</table>
						</div>
					</div>
				)}
				{!cs && kyc && (
					<p className={css.muted}>
						Next: {kyc.reviewDraft?.revisionNo ? (kyc.reviewDraft.revisionNo > 1 ? `confirm Revision ${kyc.reviewDraft.revisionNo} of your review draft by OTP.` : "your review draft is ready. Read it and confirm by OTP.") : ""} Your CRM checks your KYC once, at Final Review. You get an email and WhatsApp with the amounts.
					</p>
				)}
			</Shell>
		);
	}

	// ---- please confirm your choice (mLoyFinal)
	if (kind === "final") {
		const loy = pick === "loy";
		const c = effect?.unitCost;
		const next = (card?.reviewDraft?.revisionNo || 0) >= 1 ? "confirm the new revision of your review draft by OTP" : "read your review draft and confirm it by OTP";
		const withSave = withLoy ? (after.total || 0) - (withLoy.total || 0) : 0;
		return (
			<Shell
				title="Please confirm your choice"
				onClose={onClose}
				foot={
					<>
						<Btn onClick={() => setStep("disc")}>Go back</Btn>
						{c && (
							<Btn primary onClick={() => confirm(pick)}>
								Confirm
							</Btn>
						)}
					</>
				}
			>
				<UnitHead card={card} lead={loy ? "LOYALTY" : lead} />
				<div className={loy ? css.good : css.info}>
					<b>{loy ? "You have chosen to take the loyalty discount on your unit." : `You have chosen not to take the loyalty discount on your unit${ref ? " and to keep the referral" : ""}.`}</b>{" "}
					{loy
						? `${inr(L.amount)} comes off your total unit cost.${ref ? ` Your booking becomes a Loyalty booking and ${fn} does not earn ${ptsTxt}.` : ""}`
						: `Your unit stays at the actual unit price${ref ? `; ${fn} earns ${ptsTxt} in VJOP after the agreement is registered` : ""}. With the discount you would pay ${inr(withSave)} less.`}
				</div>
				{c ? (
					<div className={css.panel}>
						<h4>Your unit cost after this choice</h4>
						<div className={css.tw}>
							<table className={css.tbl}>
								<tbody>
									<tr>
										<th>Item</th>
										<th className={css.rt}>Amount</th>
									</tr>
									<tr>
										<td>Agreement value</td>
										<td className={css.rt}>{inr(c.av)}</td>
									</tr>
									<tr>
										<td>GST</td>
										<td className={css.rt}>{inr(c.gst)}</td>
									</tr>
									<tr>
										<td>{SDRN}</td>
										<td className={css.rt}>{inr(c.sdr)}</td>
									</tr>
									<tr className={css.lcT}>
										<td>Total unit cost</td>
										<td className={css.rt}>{inr(c.total)}</td>
									</tr>
									<tr>
										<td>Booking type</td>
										<td className={css.rt}>{effect.bookingType}</td>
									</tr>
									{ref && (
										<tr>
											<td>VJOP referral points for {fn}</td>
											<td className={css.rt}>{loy ? "0" : Number(effect.points || 0).toLocaleString("en-IN")}</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				) : (
					<p className={css.muted}>Working out your unit cost…</p>
				)}
				<p className={css.muted}>After you confirm, you go back to your dashboard. Next step: {next}. Your review draft and cost sheet use this price.</p>
			</Shell>
		);
	}

	// ---- Add back / Change to Referral (mPick2)
	const toRef = pick === "toref";
	const c = effect?.unitCost;
	return (
		<Shell
			title="Please confirm your choice"
			onClose={onClose}
			foot={
				<>
					<Btn onClick={() => setStep("disc")}>Go back</Btn>
					{(!toRef || c) && (
						<Btn primary onClick={() => confirm(toRef ? "toref" : "addback")}>
							Confirm
						</Btn>
					)}
				</>
			}
		>
			<UnitHead card={card} lead={lead} />
			<div className={css.info}>
				<b>{toRef ? "You have chosen to change your booking from Loyalty to Referral." : `You have chosen to add ${ex} back as an applicant.`}</b>{" "}
				{toRef ? `No loyalty discount. ${fn} earns ${ptsTxt} in VJOP after the agreement is registered (Sales App and VJOP are updated).` : "Your Agreement KYC opens with them added. When you save, the loyalty check runs again."}
			</div>
			{toRef && c && (
				<div className={css.panel}>
					<h4>Your unit cost after this choice</h4>
					<div className={css.tw}>
						<table className={css.tbl}>
							<tbody>
								<tr>
									<th>Item</th>
									<th className={css.rt}>Amount</th>
								</tr>
								<tr>
									<td>Agreement value</td>
									<td className={css.rt}>{inr(c.av)}</td>
								</tr>
								<tr>
									<td>GST</td>
									<td className={css.rt}>{inr(c.gst)}</td>
								</tr>
								<tr>
									<td>{SDRN}</td>
									<td className={css.rt}>{inr(c.sdr)}</td>
								</tr>
								<tr className={css.lcT}>
									<td>Total unit cost</td>
									<td className={css.rt}>{inr(c.total)}</td>
								</tr>
								<tr>
									<td>Booking type</td>
									<td className={css.rt}>Referral</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			)}
			<p className={css.muted}>After you confirm, you go back to your dashboard.</p>
		</Shell>
	);
}

// card entry (crm2CardModals.openDiscount): loads the saved view, then the flow; mode = the start step
export default function Crm2DiscountModal({ allotment, card, mode, onClose, onDone }) {
	const apId = allotment.allotmentPaymentId;
	const [view, setView] = useState(null);
	useEffect(() => {
		getCrm2Discounts(apId)
			.then((r) => setView(r?.view || null))
			.catch((err) => {
				errorTost(err.message);
				onClose();
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apId]);
	if (!view) return null;
	return <DiscountFlow apId={apId} card={{ ...card, allotment }} view={view} start={mode || "disc"} onClose={onClose} onDone={onDone} />;
}
